import type { PlayerCharacter } from "@engine/types/entity.js";

/**
 * Mutable runtime store for the active party.
 * Not an XState actor — this is shared mutable state intentionally,
 * since HP, inventory, and buffs are updated frequently by combat math.
 * UI components read this via the usePartyStore hook (to be implemented).
 */

let members: PlayerCharacter[] = [];
let gold = 0;
/** Accumulated in-game playtime in milliseconds (P14). Increments only while world is ticking. */
let playtimeMs = 0;
/**
 * Party identity (P18). Minted at chargen or on save-load; survives across
 * floor transitions via `toJSON`/`loadJSON`. See `specs/persistence.md`
 * §"Party identity on the save" for why the save picker groups by partyId.
 * `null` outside an active adventure (on the launch screen, between runs).
 */
let partyId: string | null = null;
let partyName: string | null = null;

/**
 * Designated party voice — the member who speaks for the party in every
 * dialogue knot and dialogue skill check. Null = unset; `getVoice()`
 * falls back to the first living member so dialogue never prompts and
 * never blocks. Persisted via `toJSON`/`loadJSON` so the choice rides
 * save files.
 */
let voiceId: string | null = null;

export interface PartyStoreJSON {
  members: PlayerCharacter[];
  gold: number;
  /** Accumulated in-game playtime in milliseconds (P14). */
  playtimeMs: number;
  /** Party identity (P18). Optional for back-compat with pre-P18 snapshots. */
  partyId?: string | null;
  partyName?: string | null;
  /** Designated party voice (dialogue speaker). Optional for back-compat. */
  voiceId?: string | null;
}

export const PartyStore = {
  // ── Members ────────────────────────────────────────────────────────────────

  getMembers(): readonly PlayerCharacter[] {
    return members;
  },

  setMembers(party: PlayerCharacter[]): void {
    members = [...party];
  },

  /** Append a character to the party. Used by the new-game chargen flow (C13). */
  addMember(character: PlayerCharacter): void {
    members = [...members, character];
  },

  getMember(instanceId: string): PlayerCharacter | undefined {
    return members.find((m) => m.instanceId === instanceId);
  },

  updateMember(instanceId: string, patch: Partial<PlayerCharacter>): void {
    members = members.map((m) =>
      m.instanceId === instanceId ? { ...m, ...patch } : m,
    );
  },

  // ── Formation (U06) ────────────────────────────────────────────────────────
  //
  // The members-array order IS the formation. Indices 0,1 = front row,
  // 2,3 = back row. A smaller party fills slots 0..N-1 in order; empty
  // trailing slots mean "no character in that position" (e.g. a 3-person
  // party has one empty back-row slot). Mobile HUD's 2×2 card grid
  // renders row 0 (front) on top and row 1 (back) below.
  //
  // Combat-mechanic consequences of row (reach, enemy aggression) live in
  // the combat-physical spec and will read these accessors — the model is
  // data-only for now.

  /** Front row party members (array indices 0 + 1). Length ≤ 2. */
  getFrontRow(): readonly PlayerCharacter[] {
    return members.slice(0, 2);
  },

  /** Back row party members (array indices 2 + 3). Length ≤ 2. */
  getBackRow(): readonly PlayerCharacter[] {
    return members.slice(2, 4);
  },

  /** `'front' | 'back' | null` — null if the character is not in the party. */
  getRow(instanceId: string): "front" | "back" | null {
    const idx = members.findIndex((m) => m.instanceId === instanceId);
    if (idx === -1) return null;
    return idx < 2 ? "front" : "back";
  },

  /**
   * Swap two formation slots. No-op when either index is out of range for
   * the current party size, when both indices are equal, or when swapping
   * would move an undefined slot (indices ≥ members.length are ignored).
   * Safe to call during combat — data-only, no event emission yet.
   */
  rearrange(i: number, j: number): void {
    if (i === j) return;
    if (i < 0 || j < 0) return;
    if (i >= members.length || j >= members.length) return;
    const next = [...members];
    const a = next[i]!;
    next[i] = next[j]!;
    next[j] = a;
    members = next;
  },

  // ── Spells (S01) ──────────────────────────────────────────────────────────

  /**
   * Add a spell to a character's known-spell list.
   * No-ops if the character is not found or already knows the spell.
   */
  addSpell(instanceId: string, spellId: string): void {
    members = members.map((m) => {
      if (m.instanceId !== instanceId) return m;
      const known = m.knownSpells ?? [];
      if (known.includes(spellId)) return m;
      return { ...m, knownSpells: [...known, spellId] };
    });
  },

  /**
   * Remove a spell from a character's known-spell list.
   * No-ops if the character is not found or does not know the spell.
   */
  removeSpell(instanceId: string, spellId: string): void {
    members = members.map((m) => {
      if (m.instanceId !== instanceId) return m;
      const known = m.knownSpells ?? [];
      return { ...m, knownSpells: known.filter((s) => s !== spellId) };
    });
  },

  // ── XP (W08) ──────────────────────────────────────────────────────────────

  /**
   * Award `amount` XP to every party member.
   * Negative or zero amounts are ignored.
   * Level-up threshold checking is handled by the level-up resolver (W09).
   */
  awardXp(amount: number): void {
    if (amount <= 0) return;
    members = members.map((m) => ({ ...m, xp: (m.xp ?? 0) + amount }));
  },

  // ── Playtime tracker (P14) ────────────────────────────────────────────────

  /**
   * Advance the in-game playtime by `deltaSec` seconds.
   * Called every frame from the game loop, gated on `selectIsWorldTicking`.
   * Negative or zero deltas are ignored.
   */
  addPlaytime(deltaSec: number): void {
    if (deltaSec <= 0) return;
    playtimeMs += deltaSec * 1000;
  },

  /** Accumulated in-game playtime in milliseconds. */
  getPlaytimeMs(): number {
    return playtimeMs;
  },

  /** Accumulated in-game playtime rounded down to whole seconds. */
  getPlaytimeSeconds(): number {
    return Math.floor(playtimeMs / 1000);
  },

  // ── Party identity (P18) ──────────────────────────────────────────────────

  /** UUID-ish identifier for the current party. Null outside an active run. */
  getPartyId(): string | null {
    return partyId;
  },

  /** Player-entered party name; null until chargen commits it. */
  getPartyName(): string | null {
    return partyName;
  },

  /**
   * Set both identity fields atomically. Called by chargen finalize
   * (new party), save-load (restores whatever the save carried), and the
   * reuse-party path (fresh UUID, cloned name).
   */
  setPartyIdentity(id: string, name: string): void {
    partyId = id;
    partyName = name;
  },

  // ── Gold purse ─────────────────────────────────────────────────────────────

  /** Current shared party gold total (always ≥ 0). */
  getGold(): number {
    return gold;
  },

  /**
   * Add gold to the party purse.
   * Negative or zero amounts are ignored.
   * Callers should emit `inventory:gold_changed` after this call.
   */
  earnGold(amount: number): void {
    if (amount <= 0) return;
    gold += amount;
  },

  /**
   * Deduct gold from the party purse.
   * Returns `true` and deducts when the party can afford it; otherwise
   * returns `false` without mutating the balance.
   * Callers should emit `inventory:gold_changed` on a `true` return.
   */
  spendGold(amount: number): boolean {
    if (amount < 0) return false;
    if (amount === 0) return true;
    if (gold < amount) return false;
    gold -= amount;
    return true;
  },

  /** Returns true when the party has at least `amount` gold. Non-mutating. */
  canAfford(amount: number): boolean {
    return amount >= 0 && gold >= amount;
  },

  // ── Party voice ────────────────────────────────────────────────────────────

  /** Raw designated voice id; null = unset (resolution falls back to slot 0). */
  getVoiceId(): string | null {
    return voiceId;
  },

  /**
   * Designate who speaks for the party. Pass null to clear. Ids not in
   * the current roster are ignored so a stale UI can't dangle the store.
   */
  setVoice(instanceId: string | null): void {
    if (instanceId !== null && !members.some((m) => m.instanceId === instanceId)) return;
    voiceId = instanceId;
  },

  /**
   * Resolve the effective party voice: the designated member while they
   * live, else the first living member, else the first member, else
   * null. Dialogue never blocks on a dead or dangling designation.
   */
  getVoice(): PlayerCharacter | null {
    const designated = voiceId ? members.find((m) => m.instanceId === voiceId) : undefined;
    if (designated && designated.hp > 0) return designated;
    return members.find((m) => m.hp > 0) ?? members[0] ?? null;
  },

  // ── Serialisation ──────────────────────────────────────────────────────────

  toJSON(): PartyStoreJSON {
    return { members: [...members], gold, playtimeMs, partyId, partyName, voiceId };
  },

  loadJSON(data: PartyStoreJSON): void {
    members = [...data.members];
    gold = data.gold ?? 0;
    playtimeMs = data.playtimeMs ?? 0;
    partyId = data.partyId ?? null;
    partyName = data.partyName ?? null;
    voiceId = data.voiceId ?? null;
  },

  /** Reset all state. Intended for test teardown. */
  _reset(): void {
    members = [];
    gold = 0;
    playtimeMs = 0;
    partyId = null;
    partyName = null;
    voiceId = null;
  },
};
